import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { Dispatch } from '@reduxjs/toolkit';
import axiosInstance, { ApiKeys, getApiUrl } from 'src/utils/ApiHelper';
import {
  CreateCallBridgeInteraction,
  ECallingType,
  FeedbackFormAction,
  FeedbackFormKind,
  FeedbackPayload,
  InteractionType,
  QuestionResponse
} from '../interfaces/index.type';
import {
  setSkipTracingInitialQuestions,
  storeFeedbackQuestionTypes
} from '../reducer/feedbackSlice';
import { imageType } from '../component/ImageUpload';
import { isFunction } from '@cp/src/utils/commonUtils';
import { SWW_ERROR_MESSAGE } from '@cp/constants/Global';
import { SKIP_TRACING_TYPES } from '@cp/pages/CaseDetails/feedbackForm/constants';

export const getQuestion =
  ({
    loanaccountnumber,
    customerreferenceid,
    interactionType
  }: {
    loanaccountnumber: string;
    customerreferenceid: string;
    interactionType: InteractionType | ECallingType;
  }) =>
  (dispatch: Dispatch) => {
    let params: any = { interactionType };
    if (interactionType === InteractionType.HUMAN_REMINDER)
      params = { ...params, loanAccountNumber: loanaccountnumber };
    axiosInstance
      .get(getApiUrl(ApiKeys.FETCH_QUESTIONS), {
        headers: {
          customerreferenceid,
          loanaccountnumber,
          donotHandleError: true,
          includeInUptimeMetrics: true
        },
        params
      })
      .then(res => {
        dispatch(storeFeedbackQuestionTypes(res.data));
      });
  };

export const getSkipTracingQuestions =
  ({
    loanaccountnumber,
    customerreferenceid,
    interactionType,
    dispatchFeedback
  }: {
    loanaccountnumber: string;
    customerreferenceid: string;
    interactionType: SKIP_TRACING_TYPES;
    dispatchFeedback: React.Dispatch<FeedbackFormAction>;
  }) =>
  (dispatch: Dispatch) => {
    dispatchFeedback({
      type: FeedbackFormKind.SET_LOADING,
      payload: { loading: true }
    });
    axiosInstance
      .get(getApiUrl(ApiKeys.FETCH_QUESTIONS), {
        headers: {
          customerreferenceid,
          loanaccountnumber,
          donotHandleError: true,
          includeInUptimeMetrics: true
        },
        params: { interactionType }
      })
      .then(res => {
        dispatch(setSkipTracingInitialQuestions(res.data));
      })
      .finally(() => {
        dispatchFeedback({
          type: FeedbackFormKind.SET_LOADING,
          payload: { loading: false }
        });
      });
  };

export const fetchSelectedQption = (
  loanaccountnumber: string,
  customerreferenceid: string,
  referenceId: string,
  sucessCallback: (data: Array<QuestionResponse>, referenceId: string, questionId?: string) => void,
  interactionType: InteractionType | SKIP_TRACING_TYPES = InteractionType.SELF_CALL,
  failureCallback: () => void = () => {},
  questionId?: string
) => {
  axiosInstance
    .get(getApiUrl(ApiKeys.FETCH_OPTIONS), {
      headers: {
        customerreferenceid,
        loanaccountnumber,
        donotHandleError: true,
        includeInUptimeMetrics: true
      },
      params: { interactionType, referenceId }
    })
    .then(res => {
      sucessCallback(res.data, referenceId, questionId);
    })
    .catch(failureCallback);
};

export const submitForm = (
  loanaccountnumber: string,
  customerreferenceid: string,
  body: FeedbackPayload,
  sucessCallback?: () => void,
  failureCallback?: () => void
) => {
  axiosInstance
    .post(getApiUrl(ApiKeys.SUBMIT_FEEDBACK), body, {
      headers: {
        customerreferenceid,
        loanaccountnumber,
        donotHandleError: true,
        includeInUptimeMetrics: true
      }
    })
    .then(() => {
      toast('Feedback submitted successfully', { type: 'success' });
      sucessCallback && sucessCallback();
    })
    .catch(err => {
      let errorMessage = 'Something went wrong';
      if (err?.code === 'ERR_BAD_REQUEST') {
        errorMessage = err?.response?.data?.message?.replace('error : ', '');
      }

      toast(errorMessage, { type: 'error' });
      failureCallback && failureCallback();
    });
};

export const submitFormForCallBridge = (
  loanaccountnumber: string,
  customerreferenceid: string,
  body: FeedbackPayload,
  sucessCallback: (payload) => void,
  failureCallback: (payload, error) => void,
  interactionReferenceId: string,
  headers?: Record<string, string>,
  callId?: string
) => {
  if (callId) {
    axiosInstance
      .post(getApiUrl(ApiKeys.HUMAN_REMNINDER_DISPOSE_CALL_V2), body)
      .then(() => {
        toast('Feedback submitted successfully', { type: 'success' });
        sucessCallback(body?.feedbacks);
      })
      .catch(error => {
        failureCallback(body.feedbacks, error.response?.data);
      });

    return;
  }
  axiosInstance
    .put(getApiUrl(ApiKeys.SUBMIT_INTERACTION_FEEDBACK, { interactionReferenceId }), body, {
      headers: { customerreferenceid, loanaccountnumber, includeInUptimeMetrics: true, ...headers }
    })
    .then(() => {
      toast('Feedback submitted successfully', { type: 'success' });
      sucessCallback(body?.feedbacks);
    })
    .catch(error => {
      failureCallback(body.feedbacks, error.response?.data);
    });
};

export const fetchInteraction = (
  loanAccountNumber: string,
  customerReferenceId: string,
  caseReferenceId: string,
  caseBusinessVertical: string,
  sucessCallback: (response: any) => void,
  failureCallback: () => void = () => {},
  interactionId?: string
) => {
  axiosInstance
    .get(`${getApiUrl(ApiKeys.FETCH_INTERACTION)}/${interactionId}`, {
      headers: { customerReferenceId, loanAccountNumber, includeInUptimeMetrics: true },
      params: { caseReferenceId, caseBusinessVertical }
      // params: interactionId ? { interactionId } : undefined
    })
    .then(res => {
      sucessCallback(res);
    })
    .catch(failureCallback);
};

export const createCallBridge = (
  loanAccountNumber: string,
  customerReferenceId: string,
  body: CreateCallBridgeInteraction,
  sucessCallback: (response: any) => void,
  interactionType?: InteractionType,
  callBridgeErrorCallback?: () => void
) => {
  const urlKey =
    interactionType === InteractionType.SKIP_TRACING
      ? ApiKeys.CREATE_SKIP_TRACE_CALL_BRIDGE
      : ApiKeys.CREATE_CALL_BRIDGE;
  axiosInstance
    .post(getApiUrl(urlKey), body, {
      headers: {
        loanAccountNumber,
        customerReferenceId,
        donotHandleError: true,
        includeInUptimeMetrics: true
      }
    })
    .then(res => {
      sucessCallback(res);
    })
    .catch(err => {
      const errMsg = err?.response?.data?.message || SWW_ERROR_MESSAGE;
      if (isFunction(callBridgeErrorCallback)) {
        callBridgeErrorCallback();
      }

      toast(errMsg, { type: 'error' });
    });
};

export const fetchBlankDisposition =
  (agentReferenceId: string, callBack: (res: { [key: string]: any }[]) => void) => () => {
    axiosInstance
      .get(getApiUrl(ApiKeys.BLANK_DISPOSITION), {
        headers: { agentReferenceId }
      })
      .then(res => {
        callBack(res?.data);
      })
      .catch(err => {
        toast('Validation api failed', { type: 'error' });
      });
  };

export const uploadImage = (
  formData: Array<imageType>,
  setSelectedFile: React.Dispatch<React.SetStateAction<imageType[]>>
) => {
  const url = getApiUrl(ApiKeys.UPLOAD_IMAGE_FOR_FIELD_VISIT);
  formData.map((file: imageType) => {
    const formData = new FormData();
    formData.append('image', file.imageData);
    if (file.incorrectFile) {
      return;
    }
    if (file.error) {
      setSelectedFile(prevState =>
        prevState.map((item: imageType) => {
          if (item.id === file.id) {
            return { ...item, error: false, progress: 0 };
          }
          return item;
        })
      );
    }
    axiosInstance
      .post(url, formData, {
        onUploadProgress: progressEvent => {
          const { loaded, total } = progressEvent;
          setSelectedFile(prevState =>
            prevState.map((item: imageType) => {
              if (item.id === file.id) {
                return { ...item, progress: Math.floor((loaded * 100) / total) };
              }
              return item;
            })
          );
          const percent = Math.floor((loaded * 100) / total);
        },
        cancelToken: file.cancelToken.token
      })
      .then(res => {
        setSelectedFile(prevState =>
          prevState.map((item: imageType) => {
            if (item.id === file.id) {
              return { ...item, imageUri: res.data, progress: 100, imageUploadCompleat: true };
            }
            return item;
          })
        );
      })
      .catch(() => {
        setSelectedFile(prevState =>
          prevState.map((item: imageType) => {
            if (item.id === file.id) {
              return { ...item, error: true };
            }
            return item;
          })
        );
      });
  });
};

export const uploadAudio = (
  formData: Array<imageType>,
  setSelectedFile: React.Dispatch<React.SetStateAction<imageType[]>>
) => {
  const url = getApiUrl(ApiKeys.UPLOAD_MEDIA);
  formData.map((file: imageType) => {
    const formData = new FormData();
    formData.append('media', file.imageData);
    if (file.incorrectFile) {
      return;
    }
    if (file.error) {
      setSelectedFile(prevState =>
        prevState.map((item: imageType) => {
          if (item.id === file.id) {
            return { ...item, error: false, progress: 0 };
          }
          return item;
        })
      );
    }
    axiosInstance
      .post(url, formData, {
        onUploadProgress: progressEvent => {
          const { loaded, total } = progressEvent;
          setSelectedFile(prevState =>
            prevState.map((item: imageType) => {
              if (item.id === file.id) {
                return { ...item, progress: Math.floor((loaded * 100) / total) };
              }
              return item;
            })
          );
          const percent = Math.floor((loaded * 100) / total);
        },
        cancelToken: file.cancelToken.token
      })
      .then(res => {
        setSelectedFile(prevState =>
          prevState.map((item: imageType) => {
            if (item.id === file.id) {
              return { ...item, imageUri: res.data, progress: 100, imageUploadCompleat: true };
            }
            return item;
          })
        );
      })
      .catch(() => {
        setSelectedFile(prevState =>
          prevState.map((item: imageType) => {
            if (item.id === file.id) {
              return { ...item, error: true };
            }
            return item;
          })
        );
      });
  });
};
